/**
 * Supabase Client Configuration
 *
 * Client-side Supabase client for browser usage.
 * Uses anon key (safe for client-side, RLS enforced).
 *
 * @module services/auth/supabase
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '../../types/database.types';
import { clientLogger } from '@/lib/clientLogger';

// =============================================================================
// Environment Variables
// =============================================================================

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// =============================================================================
// Types
// =============================================================================

export type SupabaseEnvStatus = {
  isConfigured: boolean;
  missing: ('VITE_SUPABASE_URL' | 'VITE_SUPABASE_ANON_KEY')[];
};

// =============================================================================
// Configuration Status
// =============================================================================

/**
 * Check Supabase environment configuration status
 */
export const getSupabaseEnvStatus = (): SupabaseEnvStatus => {
  const missing: SupabaseEnvStatus['missing'] = [];
  if (!supabaseUrl) missing.push('VITE_SUPABASE_URL');
  if (!supabaseAnonKey) missing.push('VITE_SUPABASE_ANON_KEY');
  return { isConfigured: missing.length === 0, missing };
};

// =============================================================================
// Client Singleton
// =============================================================================

let cachedClient: SupabaseClient<Database> | null = null;

/**
 * Get the Supabase client instance (singleton pattern)
 * Returns null if environment not configured (safe for dev mode without Supabase)
 */
export const getSupabaseClient = (): SupabaseClient<Database> | null => {
  // Return cached instance if available
  if (cachedClient) return cachedClient;

  // Check configuration
  if (!supabaseUrl || !supabaseAnonKey) {
    if (import.meta.env.DEV) {
      const status = getSupabaseEnvStatus();
      clientLogger.warn('[Supabase] Not configured. Missing env vars:', status.missing);
      clientLogger.warn('[Supabase] Sign-in will not work until env vars are configured.');
    }
    return null;
  }

  // In-memory lock serializer — replaces navigator.locks which causes
  // AbortErrors under React StrictMode double-mount.  navigator.locks
  // uses a 10-second timeout that fires when the first mount's lock
  // hasn't released before the second mount requests it.
  const pendingLocks = new Map<string, Promise<unknown>>();

  const inMemoryLock = async <R>(
    name: string,
    _acquireTimeout: number,
    fn: () => Promise<R>,
  ): Promise<R> => {
    const existing = pendingLocks.get(name);
    if (existing) {
      try { await existing; } catch { /* previous holder errored — proceed */ }
    }
    const promise = fn();
    pendingLocks.set(name, promise);
    try {
      return await promise;
    } finally {
      pendingLocks.delete(name);
    }
  };

  // Create and cache client
  cachedClient = createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      lock: inMemoryLock,
    },
  });

  if (import.meta.env.DEV) {
    clientLogger.info('[Supabase] Client initialized');
  }

  return cachedClient;
};

/**
 * Supabase client instance (may be null if not configured)
 * Use getSupabaseClient() for null-safe access
 */
export const supabase = getSupabaseClient();

/**
 * Check if Supabase is available and configured
 */
export const isSupabaseConfigured = (): boolean => {
  return getSupabaseEnvStatus().isConfigured;
};
